import { lazy, Suspense } from 'react';
import { Outlet } from 'react-router-dom';

import { AuthSplitLayout } from 'src/layouts/auth-split';

import { Box, Stack, Typography, IconButton } from '@mui/material';
import { LanguagePopover } from 'src/layouts/components/language-popover';
import { allLangs } from 'src/locales';
import { Iconify } from 'src/components/iconify';
import { green, red } from '@mui/material/colors';
import { NotificationsDrawer } from 'src/layouts/components/notifications-drawer';
import { pxToRem } from 'src/theme/styles';
import CustomLink from 'src/components/custom-link/link';
import { paths } from '../paths';
import i18next from 'i18next';

export const getUserRoutes = (
  pathname: string,
  token: any,
  settings: any,
  mode: any,
  setMode: any,
  bannerData: any,
  moduleList: any[],
  hasPermission: any,
  bgImages?: { bg_image?: string },
  lang?: string
) => {
  // Helper function to translate labels
  const t = (key: string) => {
    return i18next.t(key, { ns: 'form', defaultValue: key });
  };
  const userComponentMap: {
    [key: string]: {
      component: () => Promise<{ default: React.ComponentType<any> }>;
      children?: {
        [childUrl: string]: () => Promise<{ default: React.ComponentType<any> }>;
      };
    };
  } = {
    profile: {
      component: () => import('src/pages/user/profile/profile'),
      children: {
        'submodule/:sub_module': () => import('src/pages/user/user-submodule-page'),
      },
    },
    applications: {
      component: () => import('src/pages/user/applications/index'),
    },
    'available-properties': {
      component: () => import('src/pages/auth/basic/available-properties'),
    },
    conversations: {
      component: () => import('src/pages/user/conversations/index'),
    },
  };

  const userLinks = moduleList.flatMap((section: any) =>
    section.items
      .filter((item: any) => item.enabled !== false) // enabled by default
      .map((item: any) => ({
        label: t(item.name),
        url: `/${item.url}`,
      }))
  );

  const routeChildren = moduleList.flatMap((section) =>
    section.items
      .filter((item: any) => item.enabled !== false)
      .flatMap((item: any, i: number) => {
        const config = userComponentMap[item.url];
        if (!config) return [];

        const MainComponent = lazy(config.component);

        const mainRoute = {
          path: item.url,
          element: (
            <Suspense key={`${item.url}-${i}`}>
              <MainComponent />
            </Suspense>
          ),
        };

        const childRoutes =
          item.hasListMenuItems && Array.isArray(item.listMenuItems) && config.children
            ? item.listMenuItems
                .filter((sub: any) => sub.enabled !== false)
                .map((sub: any, j: number) => {
                  const subKey = sub.url.replace(/^\/+/, '');
                  const subImportFunc = config.children?.[subKey];
                  if (!subImportFunc) return null;

                  const SubComponent = lazy(subImportFunc);

                  return {
                    path: `${item.url}${sub.url}`,
                    element: (
                      <Suspense key={`${item.url}${sub.url}-${j}`}>
                        <SubComponent />
                      </Suspense>
                    ),
                  };
                })
                .filter(Boolean)
            : [];

        return [mainRoute, ...childRoutes];
      })
  );

  const headerConfig = moduleList.find((section) => section?.header)?.header || {};
  const centerAreaItems = headerConfig?.centerArea || [];
  const rawRightAreaItems = headerConfig?.rightArea || [];
  const langKey = lang === 'es' || (typeof lang === 'string' && lang.startsWith('es')) ? 'es' : 'en';
  const rightAreaItems = rawRightAreaItems.map((item: any) => {
    if (item?.helpUrlByLang && typeof item.helpUrlByLang === 'object') {
      const byLang = item.helpUrlByLang[langKey];
      return { ...item, href: byLang?.url ?? item.href, label: byLang?.label ?? item.label };
    }
    return item;
  });

  return [
    {
      element: (
        <AuthSplitLayout
          section={{
            bgImage: bgImages?.bg_image || '/assets/background/mountains_bg_light.png',
            imgUrl: false,
            renderBackButton: false,
            showLogout: true,
            customSection: (
              <Box
                sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 5,
                }}
              >
                {userLinks.map((path, idx) => (
                  <CustomLink
                    key={`${path.label}-${idx}`}
                    to={path.url}
                    label={path.label}
                    style={{ color: pathname.includes(path.url) ? '#ffd400' : 'white' }}
                  />
                ))}
              </Box>
            ),
          }}
          header={{
            slots: {
              rightArea: (
                <Box display="flex" alignItems="center" gap={1.5}>
                  {rightAreaItems
                    .filter((item: any) => item?.enabled !== false)
                    .map((item: any, idx: number) => {
                      switch (item.type) {
                        case 'link':
                          return (
                            <CustomLink
                              key={`link-${idx}`}
                              to={item.href}
                              target={item.target}
                              label={t(item.label)}
                              style={{
                                fontWeight: 600,
                                lineHeight: 22 / 14,
                                fontSize: pxToRem(14),
                                color: 'inherit',
                              }}
                            />
                          );
                        case 'notifications':
                          return <NotificationsDrawer key={`notifications-${idx}`} item={item} />;
                        case 'language-switcher':
                          return <LanguagePopover key={`localization-${idx}`} data={allLangs} />;
                        case 'theme-toggle':
                          return (
                            <IconButton
                              key={`theme-${idx}`}
                              onClick={() => {
                                settings.onUpdateField(
                                  'colorScheme',
                                  mode === 'light' ? 'dark' : 'light'
                                );
                                setMode(mode === 'light' ? 'dark' : 'light');
                              }}
                            >
                              <Iconify
                                icon={
                                  mode === 'dark'
                                    ? 'line-md:moon-filled-to-sunny-filled-loop-transition'
                                    : 'line-md:moon-rising-filled-loop'
                                }
                                height={48}
                                color={mode === 'dark' ? red[400] : '-moz-initial'}
                              />
                            </IconButton>
                          );
                        default:
                          return null;
                      }
                    })}
                </Box>
              ),
              drawerHeader: (
                <Box display="flex" alignItems="center" gap={1.5}>
                  {rightAreaItems
                    .filter((item: any) => item?.enabled !== false)
                    .map((item: any, idx: number) => {
                      switch (item.type) {
                        case 'notifications':
                          return <NotificationsDrawer key={`notifications-${idx}`} item={item} />;
                        case 'language-switcher':
                          return <LanguagePopover key={`localization-${idx}`} data={allLangs} />;
                        case 'theme-toggle':
                          return (
                            <IconButton
                              key={`theme-${idx}`}
                              onClick={() => {
                                settings.onUpdateField(
                                  'colorScheme',
                                  mode === 'light' ? 'dark' : 'light'
                                );
                                setMode(mode === 'light' ? 'dark' : 'light');
                              }}
                            >
                              <Iconify
                                icon={
                                  mode === 'dark'
                                    ? 'line-md:moon-filled-to-sunny-filled-loop-transition'
                                    : 'line-md:moon-rising-filled-loop'
                                }
                                height={48}
                                color={mode === 'dark' ? red[400] : '-moz-initial'}
                              />
                            </IconButton>
                          );
                        default:
                          return null;
                      }
                    })}
                </Box>
              ),
            },
          }}
        >
          <Outlet />
        </AuthSplitLayout>
      ),
      children: [
        {
          children: routeChildren,
        },
      ],
    },
  ];
};
